"use client";

import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";

type BillingCycle = "pro_monthly" | "pro_yearly";

interface PaymentOrder {
  orderId: string;
  code: string;
  amount: number;
  amountFormatted: string;
  qrUrl: string;
}

export default function UpgradePage() {
  const { status } = useSession();
  const router = useRouter();
  const [cycle, setCycle] = useState<BillingCycle>("pro_monthly");
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "waiting" | "paid"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (status === "unauthenticated") {
      router.push("/login");
    }
  }, [status, router]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handlePay = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/payment/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planType: cycle }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Failed to create payment");
        setLoading(false);
        return;
      }
      const data: PaymentOrder = await res.json();
      setOrder(data);
      setPaymentStatus("waiting");
      setLoading(false);

      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(
            `/api/payment/status/${data.orderId}`
          );
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.status === "paid") {
              setPaymentStatus("paid");
              if (pollRef.current) clearInterval(pollRef.current);
              setTimeout(() => router.push("/dashboard"), 2000);
            }
          }
        } catch {
          // ignore polling errors
        }
      }, 3000);
    } catch {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  }, [cycle, router]);

  return (
    <div className="min-h-screen bg-background">
      {/* Page header */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h1 className="text-lg font-bold text-foreground">Upgrade to Pro</h1>
        <Link
          href="/dashboard"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors no-underline"
        >
          Back to Dashboard
        </Link>
      </div>

      <div className="p-6 max-w-lg mx-auto space-y-6">
        <p className="text-sm text-muted-foreground text-center">
          Unlimited publishing, all themes, and more.
        </p>

        {paymentStatus === "paid" ? (
          <div className="rounded-lg border-2 border-green-600 p-8 text-center space-y-3">
            <div className="text-3xl text-green-600">&#10003;</div>
            <h2 className="text-lg font-bold text-green-600">
              Payment confirmed!
            </h2>
            <p className="text-sm text-muted-foreground">
              Redirecting to dashboard...
            </p>
          </div>
        ) : order && paymentStatus === "waiting" ? (
          <div className="rounded-lg border border-border p-6 text-center space-y-6">
            <h2 className="text-base font-bold text-foreground">
              Scan to pay
            </h2>

            {/* QR Code */}
            <div className="inline-block p-4 border border-border rounded-lg">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={order.qrUrl}
                alt="VietQR payment code"
                width={280}
                height={280}
                className="block"
              />
            </div>

            {/* Payment details */}
            <div className="rounded-lg border border-border p-4 text-left text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-semibold text-foreground">
                  {order.amountFormatted}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment code</span>
                <span className="font-semibold text-foreground font-mono tracking-wider">
                  {order.code}
                </span>
              </div>
              <div className="border-t border-border pt-3 mt-2 text-xs text-muted-foreground leading-relaxed">
                Transfer the exact amount with the payment code as the transfer
                description. The QR code includes all details.
              </div>
            </div>

            {/* Waiting indicator */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-foreground animate-pulse" />
              Waiting for payment...
            </div>
          </div>
        ) : (
          <>
            {/* Billing toggle */}
            <div className="flex gap-1 max-w-xs mx-auto border border-border rounded-lg p-1">
              <button
                onClick={() => setCycle("pro_monthly")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer ${
                  cycle === "pro_monthly"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setCycle("pro_yearly")}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer ${
                  cycle === "pro_yearly"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Yearly
                <span className="ml-1.5 text-[0.7rem] text-green-600 font-semibold">
                  -17%
                </span>
              </button>
            </div>

            {/* Price card */}
            <div className="rounded-lg border-2 border-foreground p-8 text-center space-y-6">
              <div>
                <div className="text-3xl font-bold text-foreground tracking-tight">
                  {cycle === "pro_monthly" ? "49.000" : "490.000"}
                  <span className="text-base font-normal text-muted-foreground">
                    d
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {cycle === "pro_monthly"
                    ? "per month"
                    : "per year (save 2 months)"}
                </p>
              </div>

              <ul className="text-left text-sm text-foreground space-y-2">
                {[
                  "Unlimited published documents",
                  "All themes and color presets",
                  "Custom slugs",
                  "PDF export",
                  "Remove branding",
                  "1 GB image uploads",
                  "Unlimited version history",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="text-green-600 font-semibold">
                      &#10003;
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={handlePay}
                disabled={loading}
                className="w-full py-3 px-6 rounded-lg bg-foreground text-background text-base font-medium disabled:opacity-70 disabled:cursor-not-allowed hover:bg-foreground/90 transition-colors cursor-pointer"
              >
                {loading ? "Creating payment..." : "Pay now"}
              </button>

              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
            </div>

            <p className="text-center text-xs text-muted-foreground leading-relaxed">
              Payment via VietQR bank transfer. Your plan activates instantly
              after payment is confirmed.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
